import { Link } from 'react-router-dom';
import { Sun, Moon, CalendarCheck, Brain, Globe, Star, ShieldCheck, Lock, CheckCircle } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';

/* CSS variable map: dark vs light */
export const themes = {
  dark: {
    bg: '#08080a', bg2: '#0f0f12', bg3: '#16161a', bg4: '#1c1c21',
    surface: 'rgba(255,255,255,.03)', surface2: 'rgba(255,255,255,.05)', surface3: 'rgba(255,255,255,.08)',
    border: 'rgba(255,255,255,.06)', border2: 'rgba(255,255,255,.1)', border3: 'rgba(255,255,255,.16)',
    text: '#f0f0f0', text2: '#a0a0a8', text3: '#6b6b76', text4: '#3a3a42',
    accent: '#ffffff', accent2: '#c0c0c8',
    gradBtn: 'linear-gradient(135deg,#ffffff,#d4d4d8)',
    gradText: 'linear-gradient(135deg,#ffffff 0%,#a0a0a8 100%)',
    gradGlow: 'rgba(255,255,255,.04)',
    gradPanel: 'linear-gradient(160deg,#0f0f14 0%,#0a0a0e 50%,#08080c 100%)',
    inputBg: 'rgba(255,255,255,.03)', inputBorder: 'rgba(255,255,255,.08)', inputFocus: 'rgba(255,255,255,.2)',
    error: '#f87171', success: '#4ade80',
    ringC: 'rgba(255,255,255,.04)', dotC: 'rgba(255,255,255,.5)', lineC: 'rgba(255,255,255,.03)',
  },
  light: {
    bg: '#fafafa', bg2: '#f0f0f2', bg3: '#e8e8ec', bg4: '#dddde2',
    surface: 'rgba(0,0,0,.02)', surface2: 'rgba(0,0,0,.04)', surface3: 'rgba(0,0,0,.06)',
    border: 'rgba(0,0,0,.06)', border2: 'rgba(0,0,0,.1)', border3: 'rgba(0,0,0,.16)',
    text: '#111113', text2: '#55555c', text3: '#88888f', text4: '#b8b8bf',
    accent: '#0a0a0b', accent2: '#3a3a42',
    gradBtn: 'linear-gradient(135deg,#0a0a0b,#2a2a30)',
    gradText: 'linear-gradient(135deg,#0a0a0b 0%,#55555c 100%)',
    gradGlow: 'rgba(0,0,0,.03)',
    gradPanel: 'linear-gradient(160deg,#f0f0f5 0%,#e8e8ee 50%,#f5f5f7 100%)',
    inputBg: 'rgba(0,0,0,.02)', inputBorder: 'rgba(0,0,0,.1)', inputFocus: 'rgba(0,0,0,.22)',
    error: '#dc2626', success: '#16a34a',
    ringC: 'rgba(0,0,0,.04)', dotC: 'rgba(0,0,0,.35)', lineC: 'rgba(0,0,0,.04)',
  },
};

export default function AuthLayout({ children, rightPanel }) {
  const { theme, toggle } = useTheme();
  const t = themes[theme] || themes.dark;

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Inter', sans-serif", background: t.bg, color: t.text }}>
      {/* ===== LEFT PANEL ===== */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden" style={{ background: t.gradPanel }}>
        {/* Gradient orbs */}
        <div className="absolute top-[10%] left-[20%] w-[400px] h-[400px] rounded-full blur-[120px] pointer-events-none" style={{ background: `radial-gradient(circle,${t.gradGlow},transparent 70%)` }} />
        <div className="absolute bottom-[15%] right-[15%] w-[300px] h-[300px] rounded-full blur-[100px] pointer-events-none" style={{ background: `radial-gradient(circle,${t.gradGlow},transparent 70%)` }} />

        {/* Grid pattern */}
        <div className="absolute inset-0" style={{ opacity: 0.4, backgroundImage: `linear-gradient(${t.lineC} 1px,transparent 1px),linear-gradient(90deg,${t.lineC} 1px,transparent 1px)`, backgroundSize: '48px 48px' }} />

        {/* Rings */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border pointer-events-none" style={{ width: 500, height: 500, borderColor: t.ringC, animation: 'spin 60s linear infinite' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border pointer-events-none" style={{ width: 350, height: 350, borderColor: t.ringC, animation: 'spin 45s linear infinite reverse' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border pointer-events-none" style={{ width: 200, height: 200, borderColor: t.ringC, animation: 'spin 30s linear infinite' }} />

        {/* Floating cards */}
        <div className="absolute top-[12%] left-[8%] p-4 w-64 rounded-2xl border backdrop-blur-md overflow-hidden" style={{ background: t.surface, borderColor: t.border, animation: 'float1 8s ease-in-out infinite' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: t.gradBtn }}><CalendarCheck className="w-3.5 h-3.5" style={{ color: t.bg }} /></div>
            <div><div className="text-xs font-semibold" style={{ color: t.text }}>Team Standup</div><div className="text-[10px]" style={{ color: t.text4 }}>9:00 – 9:30 AM</div></div>
          </div>
          <div className="flex -space-x-2">
            <div className="w-5 h-5 rounded-full border" style={{ background: t.surface2, borderColor: t.bg2 }} />
            <div className="w-5 h-5 rounded-full border" style={{ background: t.surface3, borderColor: t.bg2 }} />
            <div className="w-5 h-5 rounded-full border text-[7px] flex items-center justify-center font-bold" style={{ background: t.surface3, borderColor: t.bg2, color: t.text }}>+4</div>
          </div>
        </div>

        <div className="absolute top-[55%] right-[6%] p-4 w-56 rounded-2xl border backdrop-blur-md overflow-hidden" style={{ background: t.surface, borderColor: t.border, animation: 'float2 9s ease-in-out infinite' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: t.surface2 }}><Brain className="w-3.5 h-3.5" style={{ color: t.text2 }} /></div>
            <div><div className="text-xs font-semibold" style={{ color: t.text }}>AI Suggestion</div><div className="text-[10px]" style={{ color: t.text4 }}>Tue 2pm works for all</div></div>
          </div>
        </div>

        <div className="absolute bottom-[10%] left-[12%] p-4 w-52 rounded-2xl border backdrop-blur-md overflow-hidden" style={{ background: t.surface, borderColor: t.border, animation: 'float3 10s ease-in-out infinite' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: t.surface2 }}><Globe className="w-3.5 h-3.5" style={{ color: t.text2 }} /></div>
            <div><div className="text-xs font-semibold" style={{ color: t.text }}>3 Time Zones</div><div className="text-[10px]" style={{ color: t.text4 }}>Auto-synced</div></div>
          </div>
        </div>

        {/* Center brand */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 pointer-events-none">
          <div className="flex items-center gap-3 mb-6" style={{ animation: 'fadeUp .7s .1s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: t.gradBtn }}><CalendarCheck className="w-5 h-5" style={{ color: t.bg }} /></div>
            <Link to="/" className="text-2xl font-bold tracking-tight pointer-events-auto" style={{ color: t.text }}>ScheduleX</Link>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-center leading-tight mb-4 px-8" style={{ animation: 'fadeUp .7s .2s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
            <span style={{ background: t.gradText, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Scheduling made simple</span>
          </h1>
          <p className="text-center max-w-sm px-8 text-[15px] leading-[1.7]" style={{ color: t.text3, animation: 'fadeUp .7s .3s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
            Eliminate the back-and-forth. Find the perfect time for everyone, every time.
          </p>
        </div>

        {/* Bottom stats */}
        <div className="absolute bottom-8 left-8 right-8" style={{ animation: 'fadeUp .7s .5s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
          <div className="flex items-center gap-8 p-5 rounded-2xl border backdrop-blur-md" style={{ background: t.surface, borderColor: t.border }}>
            <div className="flex-1 text-center"><div className="text-xl font-bold" style={{ color: t.text }}>10K+</div><div className="text-[10px] uppercase tracking-wider font-medium" style={{ color: t.text4 }}>Users</div></div>
            <div className="w-px h-8" style={{ background: t.border }} />
            <div className="flex-1 text-center"><div className="text-xl font-bold" style={{ color: t.text }}>500K</div><div className="text-[10px] uppercase tracking-wider font-medium" style={{ color: t.text4 }}>Bookings</div></div>
            <div className="w-px h-8" style={{ background: t.border }} />
            <div className="flex-1 text-center"><div className="text-xl font-bold" style={{ color: t.text }}>99.9%</div><div className="text-[10px] uppercase tracking-wider font-medium" style={{ color: t.text4 }}>Uptime</div></div>
            <div className="w-px h-8" style={{ background: t.border }} />
            <div className="flex-1 text-center">
              <div className="text-xl font-bold flex items-center justify-center gap-1" style={{ color: t.text }}>4.9<Star className="w-3.5 h-3.5" style={{ fill: t.accent }} /></div>
              <div className="text-[10px] uppercase tracking-wider font-medium" style={{ color: t.text4 }}>Rating</div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== RIGHT PANEL ===== */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative">
        <div className="absolute top-1/3 right-0 w-[400px] h-[400px] rounded-full blur-[150px] pointer-events-none" style={{ background: `radial-gradient(circle,${t.gradGlow},transparent 70%)` }} />
        <div className="w-full max-w-[400px] relative z-10">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-10" style={{ animation: 'fadeUp .7s .1s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: t.gradBtn }}><CalendarCheck className="w-[18px] h-[18px]" style={{ color: t.bg }} /></div>
            <Link to="/" className="text-xl font-bold tracking-tight" style={{ color: t.text }}>ScheduleX</Link>
          </div>

          {/* Theme toggle */}
          <div className="flex justify-end mb-6" style={{ animation: 'fadeUp .7s .1s cubic-bezier(.16,1,.3,1) forwards', opacity: 0 }}>
            <button onClick={toggle} className="w-11 h-6 rounded-full relative cursor-pointer transition-all" style={{ background: t.border2, border: `1px solid ${t.border}` }} aria-label="Toggle theme">
              <div className="absolute top-[2px] left-[2px] w-[18px] h-[18px] rounded-full flex items-center justify-center transition-all" style={{ background: t.accent, transform: theme === 'light' ? 'translateX(20px)' : 'translateX(0)' }}>
                {theme === 'dark' ? <Sun className="w-2.5 h-2.5" style={{ color: t.bg }} /> : <Moon className="w-2.5 h-2.5" style={{ color: t.bg }} />}
              </div>
            </button>
          </div>

          {rightPanel}
        </div>
      </div>

      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
        @keyframes scaleIn{from{opacity:0;transform:scale(.92)}to{opacity:1;transform:scale(1)}}
        @keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
        @keyframes float1{0%,100%{transform:translate(0,0)}50%{transform:translate(12px,-18px)}}
        @keyframes float2{0%,100%{transform:translate(0,0)}50%{transform:translate(-15px,10px)}}
        @keyframes float3{0%,100%{transform:translate(0,0)}50%{transform:translate(8px,14px)}}
      `}</style>
    </div>
  );
}
